import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { History, ChevronDown, Send, CheckCircle2, Mailbox, XCircle } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const when = (d) => (d ? new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");

const STATUS = {
  mailed: ["Mailed", "bg-emerald-500/20 text-emerald-300"],
  paid: ["Paid – ready to print", "bg-sky-500/20 text-sky-300"],
  pending: ["Awaiting payment", "bg-amber-500/20 text-amber-300"],
  superseded: ["Replaced by new link", "bg-secondary text-muted-foreground"],
  expired: ["Expired", "bg-secondary text-muted-foreground"],
};

function events(tx) {
  const ev = [{ icon: Send, at: tx.created_at, text: `Payment link sent by ${tx.created_by || "staff"}${tx.emailed_to ? ` to ${tx.emailed_to}` : ""}` }];
  if (tx.paid_at) ev.push({ icon: CheckCircle2, at: tx.paid_at, text: `Paid ${money(tx.amount)}${tx.receipt_sent_to ? " · receipt emailed" : ""}` });
  if (tx.mailed_at) ev.push({ icon: Mailbox, at: tx.mailed_at, text: `Letters mailed by ${tx.mailed_by || "staff"}` });
  if (["expired", "superseded"].includes(tx.status)) ev.push({ icon: XCircle, at: null, text: STATUS[tx.status][0] });
  return ev;
}

function Round({ tx, n }) {
  const [label, cls] = STATUS[tx.status] || [tx.status, "bg-secondary"];
  return (
    <li className="relative pl-6 pb-5 last:pb-0" data-testid={`history-round-${tx.id}`}>
      <span className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full bg-primary ring-4 ring-primary/15" />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold">Round {n}</span>
        <Badge className={`${cls} text-[10px]`} data-testid={`history-status-${tx.id}`}>{label}</Badge>
        {tx.fee_adjust === "waived" || (tx.service_fee === 0) ? <Badge className="bg-fuchsia-500/20 text-fuchsia-300 text-[10px]" data-testid={`history-waived-${tx.id}`}>Fee waived</Badge> : null}
        {tx.fee_adjust === "custom" && <Badge className="bg-indigo-500/20 text-indigo-300 text-[10px]">Custom fee</Badge>}
        <span className="text-xs font-mono text-muted-foreground ml-auto">{money(tx.amount)}</span>
      </div>
      <div className="text-[11px] text-muted-foreground mt-0.5">Service {money(tx.service_fee)} + Certified mail {money(tx.certified_mail_fee)}</div>
      <ul className="mt-2 space-y-1">
        {events(tx).map((e, i) => (
          <li key={i} className="flex items-start gap-2 text-xs">
            <e.icon className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
            <span>{e.text}</span>
            {e.at && <span className="text-muted-foreground ml-auto whitespace-nowrap">{when(e.at)}</span>}
          </li>
        ))}
      </ul>
    </li>
  );
}

export default function PaymentHistory({ history }) {
  const [open, setOpen] = useState(false);
  if (!history?.length) return null;
  const paid = history.filter((t) => t.paid_at);
  const collected = paid.reduce((s, t) => s + (t.amount || 0), 0);
  const ordered = [...history].reverse();
  return (
    <div className="mt-3 pt-3 border-t border-border/60" data-testid="payment-history">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 text-xs text-muted-foreground hover:text-white transition-colors" data-testid="payment-history-toggle">
        <History className="h-3.5 w-3.5" />
        Payment history · {paid.length} paid · {money(collected)} collected · {history.filter((t) => t.mailed_at).length} mailed
        <ChevronDown className={`h-3.5 w-3.5 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ol className="mt-4 ml-1 border-l border-border/70 pl-0 [&>li]:-ml-[5px]" data-testid="payment-history-list">
          {ordered.map((tx, i) => ({ tx, n: i + 1 })).reverse().map(({ tx, n }) => <Round key={tx.id} tx={tx} n={n} />)}
        </ol>
      )}
    </div>
  );
}
